<template>
  <div class="container-fluid p-0">
    <div v-if="userSettingsStore.data.developerMode && hasAxenitaLinkedService" class="px-2 bg-warning-subtle d-flex justify-content-between align-items-center" :title="linkedServiceHostName">
      <img src="/linked-service/logo-axenita-light.svg" alt="Axenita Logo" style="height: 24px; width: auto;" />
      <small>{{ linkedServiceHostName }}</small>
    </div>
    <div class="input-group mt-2">
      <div class="btn-group ms-2">
        <RouterLink :to="TranslationsService.i18nRoute({ name: 'consumptions' })" class="btn btn-outline-secondary" active-class="">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          <span class="d-none d-sm-inline-block ms-1">{{ $t('global.list') }}</span>
        </RouterLink>
        <button type="button" @click="createConsumption" class="btn btn-primary" :disabled="isLoading || isSaving || uiItemList.length === 0">
          <font-awesome-icon :icon="['fas', 'floppy-disk']" />
          {{ $t('action.consumeListed') }}
        </button>
        <div class="input-group-text">
          <input class="form-check-input me-2" type="checkbox" v-model="nonBillable" id="nonBillable" :disabled="!hasAxenitaLinkedService" />
          <label class="form-check-label" for="nonBillable" :title="$t('page.consumption.nonBillableDescription')">
            <span class="d-none d-sm-inline-block">{{ $t('field.consumption.nonBillable') }}</span>
            <span class="d-inline-block d-sm-none">{{ $t('field.consumption.nonBillableShort') }}</span>
          </label>
        </div>
        <button v-if="userSettingsStore.data.developerMode && extensionActive" type="button" @click="saveToExternal" class="btn btn-outline-success">
          <font-awesome-icon :icon="['fas', 'copy']" />
          {{ $t('action.copyToExtension') }}
        </button>
        <button v-else-if="userSettingsStore.data.developerMode" type="button" @click="saveToClipboard" class="btn btn-outline-primary">
          <font-awesome-icon :icon="['fas', 'copy']" />
          {{ $t('action.copyToClipboard') }}
        </button>
      </div>
    </div>
    <div class="row mt-2 mx-2 align-items-center">
      <div class="col-auto">
        <label for="patientCode" class="col-form-label">{{ $t('field.masterdata.patientCode') }}</label>
      </div>
      <div class="col-auto">
        <ScanInput v-if="!patientCode" ref="patientCodeScanInput" :instruction="$t('action.scanBarcode')" @scanned="patientCodeScanned" />
        <div v-else @click="resetPatientCode">
          <a href="#" class="link-offset-2 link-underline-melodi">
            {{ patientCode }}
          </a>
          <font-awesome-icon :icon="['fas', 'pen']" class="ps-2" transform="shrink-4" />
        </div>
      </div>
      <div class="col-auto">
        <div class="dropdown">
          <button class="btn btn-outline-primary dropdown-toggle border-start" type="button" id="pmsMandatorDropdown" data-bs-toggle="dropdown" aria-expanded="false" :disabled="isLoading" :style="selectedMandatorStyle">
            {{ selectedMandatorName || $t('field.pms.mandator') }}
          </button>
          <ul class="dropdown-menu" aria-labelledby="pmsMandatorDropdown">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="selectMandator('')">
                &lt;{{ $t('global.unset') }}&gt;
              </a>
            </li>
            <li v-for="mandator in pmsStore.mandators" :key="mandator.id">
              <a class="dropdown-item border-start" href="#" @click.prevent="selectMandator(mandator.id)" :style="`border-left: 10px solid ${mandator.rgbHex} !important;`" :class="{ 'active': pmsMandatorId === mandator.id }">
                {{ mandator.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="patientCode" class="mt-2 mx-2 align-items-center">
      <div class="input-group">
        <ScanInput ref="itemScanInput" :instruction="$t('action.scanItem')" @scanned="itemCodeScanned" />
        <div class="input-group-text" :class="{ 'bg-warning text-dark': negativeBooking }"> 
          <input type="checkbox" class="form-check-input me-2" id="negativeBooking" v-model="negativeBooking">
          <label for="negativeBooking" class="form-check-label">{{ $t('action.cancelOrderShort') }}</label>
        </div>
      </div>
    </div>
    
    <div v-if="error" class="alert alert-danger mt-2">
      {{ error }}
    </div>

    <div id="content-page-table" class="table-fix-head mt-2" :style="fixHeadCalculatedStyle">
      <table v-if="patientCode && uiItemList.length > 0" class="table table-borderless table-sm align-middle">
        <tbody>
          <template v-for="entry in uiItemList" :key="entry.id">
            <template v-if="entry.isEditing">
              <tr class="list-item item-row-one border-bottom-0" :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing }">
                <td class="mel-bar mel-bar-primary"></td>
                <td colspan="2">
                  <div class="d-flex align-items-start">
                    <font-awesome-layers fixed-width class="me-1">
                      <template v-if="entry.item.isBillable">
                        <font-awesome-icon :icon="['fas', 'coins']" class="text-success" />
                      </template>
                      <template v-else>
                        <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
                        <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
                      </template>
                    </font-awesome-layers>
                    <!-- <input class="form-check-input me-2" type="checkbox" :checked="entry.foreignItem?.trackStock ?? true" disabled /> -->
                    <ItemField :item="entry.item" :substitute-item-code="entry.foreignItem?.code" />
                  </div>
                </td>
              </tr>
              <tr class="list-item" :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing }">
                <td class="mel-bar mel-bar-primary"></td>
                <td colspan="2">
                  <div class="d-flex justify-content-between align-items-center">
                    <button class="btn btn-outline-danger me-2" type="button" @click="removeConsumedItem(entry)">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                    <QuantityField class="rounded w-auto" :class="{ 'bg-warning': !entry.isQuantityValid }" :quantity="entry.quantity" :unit-name="entry.item.baseUnitCode" :show-diff-anim="true" :show-change-controls="true" :pad-length="maxQuantityLength" v-on:update:quantity="updateQuantity(entry, $event)" :min-quantity="0" />
                  </div>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr @click="selectRow(entry)" :class="{ highlight: entry.highlight }">
                <td class="mel-bar"></td>
                <td>
                  <div>
                    <font-awesome-layers fixed-width class="me-1">
                      <template v-if="entry.item.isBillable">
                        <font-awesome-icon :icon="['fas', 'coins']" class="text-success" />
                      </template>
                      <template v-else>
                        <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
                        <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
                      </template>
                    </font-awesome-layers>
                    <!-- <input class="form-check-input me-2" type="checkbox" :checked="entry.foreignItem?.trackStock ?? true" disabled /> -->
                    <ItemField :item="entry.item" :substitute-item-code="entry.foreignItem?.code" />
                  </div>
                </td>
                <td>
                  <QuantityField class="rounded" :class="{ 'bg-body-secondary': entry.isQuantityValid, 'text-bg-warning': !entry.isQuantityValid }" :quantity="entry.quantity" :unit-name="entry.item.baseUnitCode" :show-diff-anim="true" :pad-length="maxQuantityLength" />
                </td>
              </tr>
            </template>
          </template>
        </tbody>
        <!-- </transition-group> -->
      </table>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { toast, type ToastOptions } from 'vue3-toastify';
import { useConsumptionsStore, useItemBlocksStore, useItemsStore, useUserSettingsStore, useSessionStore, useSupplierItemsStore, useTradeItemsStore, useForeignItemsStore, usePmsStore, useUserLinkedServicesStore } from "@/stores";
import { compareOptionalString, storeToExtension, checkExtensionActive, router } from "@/services";
import mainHub from '@/hubs/mainHub';
import TranslationsService from "@/services/translations";
import Throttler from "@/services/throttler";
import ScanInput from "@/components/form/ScanInput.vue";
import IGS1 from "@/models/IGS1";
import { ItemField, QuantityField } from "@/components/fields";
import SupplierSignatureItem from "@/models/frontend/SupplierSignatureItem";
import CreateConsumptionRequest, { CreateConsumptionLineRequest } from "@/models/requests/CreateConsumptionRequest";
import { IConsumption } from "@/models/responses/IConsumption";
import DraftConsumption, { DraftConsumptionItem } from "@/models/frontend/DraftConsumption";

const AXENITA_GUID = '09cfe7f9-cdc0-44d7-9664-cc28b2551392';

const i18n = useI18n();
const sessionStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const itemsStore = useItemsStore();
const tradeItemsStore = useTradeItemsStore();
const itemBlocksStore = useItemBlocksStore();
const consumptionsStore = useConsumptionsStore();
const foreignItemsStore = useForeignItemsStore();
const supplierItemsStore = useSupplierItemsStore();
const pmsStore = usePmsStore();
const userLinkedServicesStore = useUserLinkedServicesStore();

const { selectedSiteId, user } = storeToRefs(sessionStore);

const isLoading = ref(false);
const isSaving = ref(false);
const error = ref<string>('');
const wasMounted = ref(false);
const hasAxenitaLinkedService = computed(() => {
  return userLinkedServicesStore.getByLinkedServiceId(AXENITA_GUID) !== undefined;
});
const axenitaLinkedService = computed(() => {
  return userLinkedServicesStore.getByLinkedServiceId(AXENITA_GUID);
});
const linkedServiceHostName = computed(() => {
  const url = axenitaLinkedService.value?.endpointUrl;
  if (!url) return '';
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  }
  catch {
    return '';
  }
});
const saveToastId = 'save';
const draftToastId = 'draft';
const lastValidPatientCode = ref<string>('');
const negativeBooking = ref(false);

const patientCode = ref<string | undefined>();
const patientCodeScanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const pmsMandatorId = ref<string | undefined>(undefined);
const itemScanInput = ref<InstanceType<typeof ScanInput> | null>(null);

const consumedItems = ref<SelectableItem[]>([]);
const extensionActive = ref<boolean>(false);

const scanResultIncomplete = ref(false);
const itemSignatureConstruct = ref<SelectableItem | undefined>();
const nonBillable = ref(false);
const offsetTop = ref(0);

class SelectableItem extends SupplierSignatureItem {
  public highlight = false;
  public isEditing: boolean = true;
  constructor(item: SupplierSignatureItem) {
    super(
      item.item,
      item.unitCode,
      item.quantity,
      item.lot,
      item.bestBefore,
      item.expiry,
      item.isQuantityValid,
      item.trackStock,
      item.supplierItem,
      item.foreignItem
    );
  }
}

const draftSaveThrottler = new Throttler();
const throttledSaveDraft = draftSaveThrottler.throttle(
  async () => {
    await saveDraft();
  },
  [],
  10000,
  { leading: true, trailing: true }
);

const uiItemList = computed(() => [...consumedItems.value].sort((a, b) =>
  a.item.name.localeCompare(b.item.name) ||
  compareOptionalString(a.expiry, b.expiry) ||
  compareOptionalString(a.bestBefore, b.bestBefore) ||
  compareOptionalString(a.lot, b.lot)
));

const maxQuantityLength = computed(() => {
  return consumedItems.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

onMounted(async () => {
  extensionActive.value = await checkExtensionActive();

  if (hasAxenitaLinkedService.value) {
    // Load mandators from PMS store
    await pmsStore.loadMandators();
    const previouslySelectedMandatorId = localStorage.getItem('selected-pms-mandator-id');
    if (previouslySelectedMandatorId) {
      if (pmsStore.getMandatorById(previouslySelectedMandatorId)) {
        // If the mandator exists, set it
        pmsMandatorId.value = previouslySelectedMandatorId;
      }
      else {
        pmsMandatorId.value = undefined; // If not, unset it
      }
    }
    else {
      pmsMandatorId.value = undefined;
    }
  }
  else {
    pmsStore.clearMandators();
    nonBillable.value = true; // no system linked, so no billable consumption
  }

  var selectedPatientCode = sessionStorage.getItem('selected-patient-code');
  if (selectedPatientCode) {
    patientCode.value = selectedPatientCode;
    await loadDraft(selectedPatientCode);
  }

  mainHub.onScopeChanged(async (scope) => {
    const siteId = selectedSiteId.value;
    const userId = user.value?.id;
    if (scope === `sit,usr;ref:${siteId},${userId},${patientCode.value ?? ''}`) {
      console.log('owned scope changed');
      // await loadDraft();
    }
  });

  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
  wasMounted.value = true;
});

onBeforeUnmount(async () => {
  await saveDraft();
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const loadDraft = async (referenceCode: string) => {
  const draftConsumption = await consumptionsStore.loadDraft(referenceCode);
  if (draftConsumption === null) {
    nonBillable.value = false;
    consumedItems.value = [];
  }
  else {
    patientCode.value = draftConsumption.patientCode;
    nonBillable.value = draftConsumption.nonBillable ?? false;
    pmsMandatorId.value = draftConsumption.pmsMandatorId;
    consumedItems.value = draftConsumption.consumedItems.map((entry: any) => {
      const supplierItem = supplierItemsStore.getByItemId(entry.itemId);
      const foreignItem = foreignItemsStore.getByItemId(entry.itemId);
      const signatureItem = new SupplierSignatureItem(
        itemsStore.getById(entry.itemId),
        entry.baseUnitCode,
        entry.quantity,
        entry.lot,
        entry.bestBefore,
        entry.expiry,
        false,
        foreignItem?.trackStock ?? true,
        supplierItem,
        foreignItem
      );
      var selectableItem = new SelectableItem(signatureItem);
      selectableItem.isEditing = false;
      return selectableItem;
    });
  }

  if (!hasAxenitaLinkedService.value) nonBillable.value = true; // no system linked, so no billable consumption

  if (patientCode.value) {
    nextTick(() => {
      itemScanInput.value?.focus();
    });
  }
  else {
    nextTick(() => {
      patientCodeScanInput.value?.focus();
    });
  }
};

const updateQuantity = (item: SelectableItem, quantity: number) => {
  item.quantity = quantity;
};

const patientCodeScanned = async (barcode: string) => {
  error.value = '';
  const potentialPatientCode = barcode?.trim() ?? '';

  if (!nonBillable.value) {
    const isPatientNumberValid = await pmsStore.verifyPatientNumber(potentialPatientCode);
    if (!isPatientNumberValid) {
      toast.error(i18n.t('error.invalidPatientCode', { patientCode: potentialPatientCode }), { autoClose: 5000, theme: 'colored' });
      return;
    }
    else {
      lastValidPatientCode.value = potentialPatientCode; // store the last valid patient code, to prevent unnecessary re-verification
    }
  }

  patientCode.value = potentialPatientCode;

  // save patient code to browser session storage
  sessionStorage.setItem('selected-patient-code', patientCode.value);
  await loadDraft(patientCode.value);
  nextTick(() => {
    itemScanInput.value?.focus();
  });
};

const resetPatientCode = () => {
  sessionStorage.removeItem('selected-patient-code');
  patientCode.value = undefined;
  if (!hasAxenitaLinkedService.value) nonBillable.value = true; // no system linked, so no billable consumption
  else nonBillable.value = false;
  consumedItems.value = [];
  nextTick(() => {
    patientCodeScanInput.value?.focus();
  });
};

const itemCodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  scanResultIncomplete.value = false; // used to show/hide the form fields after successful scanning, but incomplete signature
  error.value = '';

  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    const tradeItem = tradeItemsStore.getByCode(contentCode);
    if (tradeItem) {
      const item = itemsStore.getById(tradeItem.itemId);
      if (item) {
        const supplierItem = supplierItemsStore.getByItemId(item.id);
        const foreignItem = foreignItemsStore.getByItemId(item.id);
        const signatureItem = new SupplierSignatureItem(item, item.baseUnitCode, 1, undefined, undefined, undefined, false, foreignItem?.trackStock ?? true, supplierItem, foreignItem);
        itemSignatureConstruct.value = new SelectableItem(signatureItem);
      }
    }
    if (!itemSignatureConstruct.value && gs1?.itemCode) {
      const item = itemsStore.getByCode(gs1.itemCode);
      if (item) {
        const supplierItem = supplierItemsStore.getByItemId(item.id);
        const foreignItem = foreignItemsStore.getByItemId(item.id);
        const signatureItem = new SupplierSignatureItem(item, item.baseUnitCode, 1, undefined, undefined, undefined, false, foreignItem?.trackStock ?? true, supplierItem, foreignItem);
        itemSignatureConstruct.value = new SelectableItem(signatureItem);
      }
    }
  }

  // if not matched already, try get item by code
  if (!itemSignatureConstruct.value) {
    const item = itemsStore.getByCode(barcode);
    if (item) {
      const supplierItem = supplierItemsStore.getByItemId(item.id);
      const foreignItem = foreignItemsStore.getByItemId(item.id);
      const signatureItem = new SupplierSignatureItem(item, item.baseUnitCode, 1, undefined, undefined, undefined, false, foreignItem?.trackStock ?? true, supplierItem, foreignItem);
      itemSignatureConstruct.value = new SelectableItem(signatureItem);
    }
  }

  if (!itemSignatureConstruct.value) {
    const itemBlock = itemBlocksStore.getByCode(barcode);
    if (itemBlock) {
      // add each part item to consumedItems
      itemBlock.partItems.forEach((partItem) => {
        const partItemItem = itemsStore.getById(partItem.itemId);
        const supplierItem = supplierItemsStore.getByItemId(partItem.itemId);
        const foreignItem = foreignItemsStore.getByItemId(partItem.itemId);
        const sigItem = new SupplierSignatureItem(
          partItemItem,
          partItemItem.baseUnitCode,
          partItem.quantity,
          undefined,
          undefined,
          undefined,
          false,
          foreignItem?.trackStock ?? true,
          supplierItem,
          foreignItem
        );
        upsertToList(new SelectableItem(sigItem));
      });
      
      negativeBooking.value = false;
      return;
    }
  }

  if (!itemSignatureConstruct.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  if (gs1?.quantity) {
    itemSignatureConstruct.value.quantity = gs1.quantity * itemSignatureConstruct.value.quantity;
  }

  upsertConstructToList();
  
  negativeBooking.value = false;
};

const upsertConstructToList = () => {
  if (itemSignatureConstruct.value === undefined) return;

  upsertToList(itemSignatureConstruct.value);
  itemSignatureConstruct.value = undefined;
};

const upsertToList = (itemSignatureConstruct: SelectableItem) => {
  if (!itemSignatureConstruct) return;
  if (!itemSignatureConstruct.isValidSignature) {
    const item = itemSignatureConstruct.item;
    toast.warn(i18n.t('error.requiresValidSignature', { itemId: item.id, tenantCode: item.tenant.code, itemCode: item.code }), { autoClose: 10000, theme: 'colored' });
    console.warn(`Signature tracking required / calculated signature disallowed #${item.id} (${item.tenant.code}/${item.code})`);
    return; 
  }

  isLoading.value = true;

  const consumableItem = itemSignatureConstruct;
  // add or update to consumedItems - update quantity if item with signature already exists
  const existingItem = consumedItems.value.find((entry) =>
    entry.item.id == consumableItem.item.id &&
    entry.lot == consumableItem.lot &&
    entry.bestBefore == consumableItem.bestBefore &&
    entry.expiry == consumableItem.expiry);
  if (existingItem) {
    const factor = negativeBooking.value ? -1 : 1;
    existingItem.quantity += factor * consumableItem.quantity;
    if (negativeBooking.value && existingItem.quantity <= 0) {
      removeConsumedItem(existingItem);
    }
  }
  else if (!negativeBooking.value) {
    consumedItems.value.push(consumableItem);
  }

  selectRow(consumableItem);
  isLoading.value = false;
  itemScanInput.value?.focus();
};

function isGuid(str: string | undefined | null): boolean {
  if (!str) return false;
  const guidRegex = /^[{(]?[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}[)}]?$/;
  return guidRegex.test(str);
}

const saveDraft = async () => {
  if (!selectedSiteId.value || !patientCode.value) return;

  toast.loading(i18n.t('page.consumption-create.consumptionSaving'), {
    toastId: draftToastId,
    autoClose: false,
    theme: toast.THEME.COLORED,

    transition: toast.TRANSITIONS.ZOOM,
    position: toast.POSITION.TOP_RIGHT,
  } as ToastOptions);

  let serverPmsMandatorId = pmsMandatorId.value;
  if (!isGuid(serverPmsMandatorId)) serverPmsMandatorId = undefined;
  const draftConsumptionItems = consumedItems.value
    .map((item) => new DraftConsumptionItem(item.item.id, item.quantity, item.lot, item.bestBefore, item.expiry))
    .sort((a, b) => a.itemId - b.itemId); // sort by itemId to ensure consistent order
  const draftConsumption = new DraftConsumption(patientCode.value ?? '', nonBillable.value, draftConsumptionItems, serverPmsMandatorId);
  await consumptionsStore.saveDraft(patientCode.value, draftConsumption, onSaveDraftApiFulfilled, onSaveDraftApiRejected);
};

function onSaveDraftApiFulfilled(): void {
  toast.remove(draftToastId);
}

function onSaveDraftApiRejected(): void | PromiseLike<void> {
  toast.remove(draftToastId);
}

const saveToExternal = async () => {
  if (!await saveToExtension()) {
    await saveToClipboard();
  }
}

// const saveToExtensionWithFallback = async () => {
//   if (extensionActive.value) {
//     if (!await saveToExtension()) {
//       await saveToClipboard();
//     }
//   }
//   else {
//     await saveToClipboard();
//   }
// }

const saveToExtension = async () => {
  return await storeToExtension('consumption', {
    patientCode: patientCode.value,
    nonBillable: nonBillable.value,
    consumedItems: getAllConsumedItems()
  });
}

const saveToClipboard = async () => {
  // const consumptionData = {
  //   patientCode: patientCode.value,
  //   consumedItems: getAllConsumedItems()
  // };
  // const consumptionDataString = JSON.stringify(consumptionData, null, 2);
  const consumptionDataString = jsonToCsv(getSlimConsumedItems());
  navigator.clipboard.writeText(consumptionDataString).then(
    () => toast.success(i18n.t('statement.contentCopied'), { autoClose: 1000 }),
    () => toast.error(i18n.t('error.copiedError'), { autoClose: 1000 })
  );
};

watch([patientCode, nonBillable, pmsMandatorId, consumedItems], async ([, newNonBillable], [, oldNonBillable]) => {
  if (!wasMounted.value) return;
  if (consumedItems.value === undefined) return;
  if (oldNonBillable === true && newNonBillable === false && patientCode.value !== undefined && patientCode.value !== lastValidPatientCode.value) {
    const isPatientNumberValid = await pmsStore.verifyPatientNumber(patientCode.value);
    if (!isPatientNumberValid) {
      toast.error(i18n.t('error.invalidPatientCode', { patientCode: patientCode.value }), { autoClose: 5000, theme: 'colored' });
      resetPatientCode();
    }
    else {
      lastValidPatientCode.value = patientCode.value; // store the last valid patient code, to prevent unnecessary re-verification
    }
  }
  throttledSaveDraft();
}, { deep: true });

watch(pmsMandatorId, async () => {
  if (isGuid(pmsMandatorId.value)) {
    localStorage.setItem('selected-pms-mandator-id', pmsMandatorId.value ?? '');
  }
  else {
    localStorage.removeItem('selected-pms-mandator-id');
  }
});

const onApiFulfilled = async (consumption: IConsumption) => {
  isSaving.value = false;
  toast.update(saveToastId, {
    render: i18n.t('statement.consumptionSaved', { consumptionReferenceCode: consumption.referenceCode }),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);
  // await saveToExtensionWithFallback();
  // Persist the now-empty draft BEFORE clearing patientCode, otherwise saveDraft
  // bails on its !patientCode guard and the previous items remain on the server,
  // resurfacing the next time the same patient code is scanned.
  consumedItems.value = [];
  await saveDraft();
  sessionStorage.removeItem('selected-patient-code');
  patientCode.value = undefined;
  if (!hasAxenitaLinkedService.value) nonBillable.value = true; // no system linked, so no billable consumption
  else nonBillable.value = false;
  setTimeout(() => {
    router.push({ name: 'consumption', params: { consumptionId: consumption.id } });
  }, 3000);
};

const onApiRejected = (error: Error) => {
  isSaving.value = false;
  toast.update(saveToastId, {
    render: i18n.t('error.apiError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
};

const createConsumption = async () => {
  if (isSaving.value || !selectedSiteId.value || !patientCode.value || consumedItems.value.length === 0) return;

  isSaving.value = true;
  toast.loading(i18n.t('status.savingEllipsis'), {
    toastId: saveToastId,
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);

  let serverPmsMandatorId = pmsMandatorId.value;
  if (!isGuid(serverPmsMandatorId)) serverPmsMandatorId = undefined;
  const lines = consumedItems.value
    .map((entry) => new CreateConsumptionLineRequest(entry.item.id, entry.quantity, entry.lot, entry.bestBefore, entry.expiry))
    .sort((a, b) => a.itemId - b.itemId); // sort by itemId to ensure consistent order
  const request = new CreateConsumptionRequest(selectedSiteId.value, patientCode.value, nonBillable.value, lines, serverPmsMandatorId);
  await consumptionsStore.create(request, onApiFulfilled, onApiRejected);
};

const selectRow = (selectedItem: SelectableItem) => {
  consumedItems.value.forEach(item => {
    item.isEditing = item === selectedItem;
  });
};

const removeConsumedItem = (item: SelectableItem) => {
  const index = consumedItems.value.indexOf(item);
  if (index !== -1) {
    consumedItems.value.splice(index, 1);
  }
};

// Computed property to get the selected mandator's name for the dropdown button
const selectedMandatorName = computed(() => {
  if (!pmsMandatorId.value) return '';
  
  const selectedMandator = pmsStore.mandators.find(m => m.id === pmsMandatorId.value);
  return selectedMandator?.name || '';
});

// Function to handle mandator selection from dropdown
const selectMandator = (mandatorId: string) => {
  pmsMandatorId.value = mandatorId || undefined;
};

// Computed property to get the selected mandator's style for the dropdown button
const selectedMandatorStyle = computed(() => {
  if (!pmsMandatorId.value) return {};
  
  const selectedMandator = pmsStore.mandators.find(m => m.id === pmsMandatorId.value);
  if (!selectedMandator?.rgbHex) return {};
  
  return {
    '--mandator-color': selectedMandator.rgbHex,
    'padding-left': '12px',
    'font-weight': '500'
  };
});

// make a list of combined consumed items and consumed item blocks
function getAllConsumedItems() {
  let consumedItemsList: {
    itemCode: string;
    itemName: string;
    itemDescription: string;
    quantity: number;
    unitCode: string;
    lot: string | undefined;
    bestBefore: string | undefined;
    expiry: string | undefined;
    trackStock: boolean;
    foreignItemCode: string | undefined;
    supplierItemCode: string | undefined;
  }[] = [];
  consumedItemsList = consumedItems.value.map((item) => {
    return {
      itemCode: item.item.code,
      itemName: item.item.name,
      itemDescription: item.item.description,
      quantity: item.quantity,
      unitCode: item.item.baseUnitCode,
      lot: item.lot,
      bestBefore: item.bestBefore,
      expiry: item.expiry,
      trackStock: item.foreignItem?.trackStock ?? true,
      foreignItemCode: item.foreignItem?.code,
      supplierItemCode: item.supplierItem?.code
    };
  });
  return consumedItemsList;
}

function getSlimConsumedItems() {
  let consumedItemsList: {
    itemCode: string;
    quantity: number;
    unitCode: string;
    itemName: string;
    itemDescription: string;
  }[] = [];
  consumedItemsList = consumedItems.value.map((item) => {
    return {
      itemCode: item.foreignItem?.code ?? item.item.code,
      quantity: item.quantity,
      unitCode: item.item.baseUnitCode,
      itemName: item.item.name,
      itemDescription: item.item.description,
    };
  });
  return consumedItemsList;
}

function jsonToCsv(jsonArray: any[]): string {
    if (!Array.isArray(jsonArray) || jsonArray.length === 0) {
        return '';
    }

    // Extract headers (keys) from the first object
    const headers = Object.keys(jsonArray[0]);
    const csvRows = [];

    // Add headers as the first row
    csvRows.push(headers.join(','));

    // Add data rows
    jsonArray.forEach(obj => {
        const row = headers.map(header => {
            const value = obj[header];
            // Escape double quotes and wrap values in quotes if necessary
            return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
        });
        csvRows.push(row.join(','));
    });

    // Combine rows with newline characters
    return csvRows.join('\n');
}
</script>

<style scoped type="scss">
tr.item-row-one {
  border-top: 1px dashed #ccc;

  &:first-child {
    border-top: none;
  }
}

@keyframes pulse {
  0% {
    background-color: inherit;
  }

  20% {
    background-color: var(--bs-primary-bg-subtle);
  }

  50% {
    background-color: var(--bs-primary-bg-subtle);
  }

  100% {
    background-color: inherit;
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-item.list-enter-active {
  animation: pulse 1s ease-in-out;
}

.itemblock-enter-active,
.itemblock-leave-active {
  transition: all 0.5s ease;
}

.itemblock-enter-from,
.itemblock-leave-to {
  opacity: 0;
  transform-origin: left;
  transform: scaleX(0);
}

.itemblock-item.itemblock-enter-active {
  animation: pulse 1s ease-in-out;
}

.itemblock-item > td {
  border-bottom: 1px dashed #ccc;
  padding-top: 0;
  padding-bottom: 6px;
  /* background-color: var(--bs-primary-bg-subtle); */
}
.itemblock-item-part-container > td{
  padding-left: 1.5rem;
  /* background-color: var(--bs-melodi-bg-subtle); */
}

/* Custom styling for mandator dropdown button */
#pmsMandatorDropdown {
  border-left: 10px solid var(--mandator-color) !important;
}

input[type=number] {
  text-align: right;
}

th {
  font-weight: bold;
}

tr {
  border-bottom: 1px dashed #ccc;

  &:last-child {
    border-bottom: none;
  }
}

th,
td {
  padding-left: .5rem;
  padding-right: .5rem;
}

@keyframes highlight-bg {
  from {
    background-color: var(--bs-melodi-bg-subtle);
  }
  to {
    background-color: var(--mel-row-filled-bg);
  }
}

.highlight {
  animation: highlight-bg 3s;
}

.row-selected > td {
  background-color: var(--mel-row-filled-bg);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent !important;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg) !important;
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary-bg-subtle) !important;
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success) !important;
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning) !important;
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger) !important;
}

.mel-absolute-warning-container {
  position: relative; /* Acts as the reference point */
  overflow: hidden;
}
.mel-absolute-warning-background {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  z-index: 0;
}
.mel-absolute-warning-foreground {
  position: relative; /* Keeps it above the background */
  z-index: 1;
}
</style>